import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import qs from 'qs';

import APIUtils from '@livongo/utils/api';

const PREFIX = 'USER';
const FETCH_AUTH_TOKEN = `${PREFIX}/fetchAuthToken`;
const FETCH_PERSONAL_INFO = `${PREFIX}/fetchPersonalInfo`;
const REDIRECT_MEMBER_PORTAL = `${PREFIX}/redirectToMemberPortal`;
const GET_USER_DETAILS = `${PREFIX}/getUserDetails`;

export const fetchAuthTokenFromOAuth2 = createAsyncThunk(
    FETCH_AUTH_TOKEN,
    async (oauth2, {dispatch, getState, rejectWithValue}) => {
        const basicAuthConfig = {
            headers: {
                Authorization: `Basic ${process.env.BASIC_AUTH}`,
                'Content-Type':
                    'application/x-www-form-urlencoded; charset=UTF-8',
            },
        };

        const formData = {
            code: oauth2,
            // eslint-disable-next-line camelcase
            grant_type: 'authorization_code',
        };

        try {
            const response = await APIUtils.post(
                'v1/users/me/auth',
                qs.stringify(formData),
                basicAuthConfig
            );

            return response.data;
        } catch (err) {
            const {status, statusText, data} = err;
            const {error, error_description: errorMessage} = data;

            // eslint-disable-next-line no-console
            console.log(
                'GetAuthTokenFromOAuth2',
                `status: ${status}`,
                `statusText: ${statusText}`,
                `error: ${error}`,
                `errorMessage: ${errorMessage}`
            );

            if (data.error) {
                return rejectWithValue({error: data.error});
            } else if (data.message) {
                return rejectWithValue({error: data.message});
            }
        }
    }
);

export const fetchPersonalInfo = createAsyncThunk(
    FETCH_PERSONAL_INFO,
    async (_, {dispatch, getState, rejectWithValue}) => {
        const {authToken} = getState().user;

        try {
            const response = await APIUtils.get(
                'v1/users/me/info/personal',
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            return response.data;
        } catch (err) {
            const {status, statusText, data} = err;
            const {error, error_description: errorMessage} = data;

            // eslint-disable-next-line no-console
            console.log(
                'FetchPersonalInfo',
                `status: ${status}`,
                `statusText: ${statusText}`,
                `error: ${error}`,
                `errorMessage: ${errorMessage}`
            );

            if (data.error) {
                return rejectWithValue({error: data.error});
            } else if (data.message) {
                return rejectWithValue({error: data.message});
            }
        }
    }
);

export const redirectToMemberPortal = createAsyncThunk(
    REDIRECT_MEMBER_PORTAL,
    async (authToken, {dispatch, getState, rejectWithValue}) => {
        const basicAuthConfig = {
            headers: {
                Authorization: `Bearer ${authToken}`,
                'Content-Type':
                    'application/x-www-form-urlencoded; charset=UTF-8',
            },
        };

        const formData = {
            jwt: authToken,
        };

        try {
            const response = await APIUtils.post(
                `v1/users/me/auth/oAuth2CodeFromJWT?jwt=${authToken}`,
                qs.stringify(formData),
                basicAuthConfig
            );

            return response.data;
        } catch (err) {
            const {status, statusText, data} = err;
            const {error, error_description: errorMessage} = data;

            // eslint-disable-next-line no-console
            console.log(
                'GetMemberPortalAccessToken',
                `status: ${status}`,
                `statusText: ${statusText}`,
                `error: ${error}`,
                `errorMessage: ${errorMessage}`
            );

            if (data.error) {
                return rejectWithValue({error: data.error});
            } else if (data.message) {
                return rejectWithValue({error: data.message});
            }
        }
    }
);

export const getUserDetails = createAsyncThunk(
    GET_USER_DETAILS,
    async (_, {dispatch, getState, rejectWithValue}) => {
        const {authToken} = getState().user;

        try {
            const response = await APIUtils.get(
                'v1/accounts/me/info',
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            return response.data;
        } catch (err) {
            const {status, statusText, data} = err;
            const {error, error_description: errorMessage} = data;

            // eslint-disable-next-line no-console
            console.log(
                'GetUserDetails',
                `status: ${status}`,
                `statusText: ${statusText}`,
                `error: ${error}`,
                `errorMessage: ${errorMessage}`
            );

            if (data.error) {
                return rejectWithValue({error: data.error});
            } else if (data.message) {
                return rejectWithValue({error: data.message});
            }
        }
    }
);

const initialState = {
    authToken: null,
    email: null,
    firstName: null,
    lastName: null,
    birthDate: null,
    address: null,
    loading: false,
    error: null,
    accessCode: null,
    details: {},
};

// Then, handle actions in your reducers:
const userSlice = createSlice({
    name: PREFIX,
    initialState,
    reducers: {
        // standard reducer logic, with auto-generated action types per reducer
        addAuthToken: {
            reducer: (state, action) => {
                state.authToken = action.payload.authToken;
            },
            prepare: authToken => {
                return {payload: {authToken}};
            },
        },
    },
    extraReducers: {
        [fetchAuthTokenFromOAuth2.pending]: (state, action) => {
            state.loading = true;
        },
        [fetchAuthTokenFromOAuth2.fulfilled]: (state, action) => {
            const {access_token: accessToken} = action.payload;

            state.authToken = accessToken;
            state.error = null;
            state.loading = false;
        },
        [fetchAuthTokenFromOAuth2.rejected]: (state, action) => {
            const {error} = action.payload;

            state.loading = false;
            state.error = error;
        },
        [fetchPersonalInfo.pending]: (state, action) => {
            state.loading = true;
        },
        [fetchPersonalInfo.fulfilled]: (state, action) => {
            const {
                email,
                firstName,
                lastName,
                birthDate,
                address,
            } = action.payload;

            state.error = null;
            state.loading = false;

            state.email = email;
            state.firstName = firstName;
            state.lastName = lastName;
            state.birthDate = birthDate;
            state.address = address;
        },
        [fetchPersonalInfo.rejected]: (state, action) => {
            const {error} = action.payload;

            state.loading = false;
            state.error = error;
        },
        [redirectToMemberPortal.pending]: (state, action) => {
            state.loading = true;
        },
        [redirectToMemberPortal.fulfilled]: (state, action) => {
            const {code} = action.payload;

            state.accessCode = code;
            state.loading = false;
        },
        [redirectToMemberPortal.rejected]: (state, action) => {
            const {error} = action.payload;

            state.loading = false;
            state.error = error;
        },
        [getUserDetails.pending]: (state, action) => {
            state.loading = true;
        },
        [getUserDetails.fulfilled]: (state, action) => {
            const {
                client: {
                    attributes: {phoneNumberRegions, addressTypes},
                },
                groups: {
                    organization: {code},
                },
            } = action.payload;

            state.error = null;
            state.loading = false;

            state.details.phoneRegions = phoneNumberRegions;
            state.details.regCode = code;
            state.details.addressTypes = addressTypes;
        },
        [getUserDetails.rejected]: (state, action) => {
            const {error} = action.payload;

            state.loading = false;
            state.error = error;
        },
    },
});

export const {addAuthToken} = userSlice.actions;

export default userSlice.reducer;
